import { format as fnsFormat, isValid, parseISO } from 'date-fns';
import moment from 'moment';

export function getDaysSince(date: string | Date): number {
    if (!date) return 0;

    let then: Date;

    if (typeof date === 'string') {
        let normalized = date.trim();

        // Case 1: RFC format like "Wed, 11 Feb 2026 22:05:26 GMT" (documents)
        if (normalized.includes('GMT')) {
            then = new Date(normalized);
        }
        // Case 2: ISO without timezone like "2026-02-11T10:17:46.262000" (clientApp)
        else if (normalized.includes('T')) {
            // Remove extra microseconds beyond milliseconds
            normalized = normalized.replace(/(\.\d{3})\d+/, '$1');

            // Ensure UTC
            if (
                !normalized.endsWith('Z') &&
                !normalized.includes('+') &&
                !normalized.match(/-\d{2}:\d{2}$/)
            ) {
                normalized += 'Z';
            }

            then = new Date(normalized);
        }
        // Fallback
        else {
            then = new Date(normalized);
        }
    } else {
        then = new Date(date);
    }

    if (Number.isNaN(then.getTime())) {
        console.error('Invalid date value:', date);
        return 0;
    }

    const now = new Date();
    const diffMs = now.getTime() - then.getTime();
    const msPerDay = 1000 * 60 * 60 * 24;

    const result = Math.floor(diffMs / msPerDay);
    return Math.max(0, result);
}

/** Existing relative-time helper (manual calculation). Prefer for current call sites. */
export const formatDistanceToNow = (date: string): string => {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
        return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
        return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }

    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
};

/**
 * Moment-based relative time (e.g. "a few seconds ago", "2 hours ago").
 */
export const formatMomentFromNow = (date: string | Date): string => {
    const m = moment(date);
    if (!m.isValid()) return '—';
    return m.fromNow();
};

/**
 * Compact calendar-style datetime for tables/lists (moment).
 * Today / yesterday / last week use relative labels; older dates show absolute.
 */
export const formatCalendarDateTime = (date: string | Date): string => {
    const m = moment(date);
    if (!m.isValid()) return '—';

    return m.calendar(null, {
        sameDay: '[Today at] HH:mm',
        lastDay: '[Yesterday at] HH:mm',
        lastWeek: 'ddd [at] HH:mm',
        sameElse: 'MMM D, YYYY [at] HH:mm',
    });
};

/** Short absolute datetime for dense table columns (moment). */
export const formatDateTime = (date: string | Date, format = 'MMM D, YYYY · HH:mm'): string => {
    const m = moment(date);
    if (!m.isValid()) return '—';
    return m.format(format);
};

/** Parse ISO / `yyyy-MM-dd` into a Date for MUI DatePicker. */
export const toDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const parsed = parseISO(value.length === 10 ? `${value}T00:00:00` : value);
    return isValid(parsed) ? parsed : null;
};

/** Format a DatePicker value as `yyyy-MM-dd` for API query params. */
export const toDateString = (value: Date | null): string =>
    value && isValid(value) ? fnsFormat(value, 'yyyy-MM-dd') : '';
